import {
	HandlingMode,
	IClientConfigs,
	IPlayerVehicleListItem,
	PlayerVehicleSource,
	VehicleClass,
	VehicleEngineType,
	VehicleFuelType,
	VehicleHandling,
	VehicleNumberPlateType,
	VehicleType,
} from '~/shared';
import { IObservable, Subject } from '../../utils';
import type {
	IVehicleService,
	IVehicleTurnLightsState,
} from './vehicleServiceTypes';

export class VehicleServiceStub implements IVehicleService {
	private readonly playerVehiclesStub: IPlayerVehicleListItem[] = [];
	private readonly workVehiclesStub: IPlayerVehicleListItem[] = [];

	private readonly playerVehicles = new Subject<IPlayerVehicleListItem[]>([]);
	private readonly workVehicles = new Subject<IPlayerVehicleListItem[]>([]);
	private readonly fuel = new Subject(12);
	private readonly fuelTankCapacity = new Subject(60);
	private readonly engineType = new Subject(VehicleEngineType.Petrol);
	private readonly mileage = new Subject(12345);

	private readonly speed = new Subject(24);
	private readonly maxSpeed = new Subject(240);
	private readonly vehicleId = new Subject<string | null>(
		'abcd-efgh-ijkl-mnop',
	);
	private readonly vehicleNetId = new Subject<number | null>(0);

	private readonly handlingMode = new Subject<HandlingMode>(
		HandlingMode.Comfort,
	);

	private readonly localVehicleType = new Subject<VehicleType>(VehicleType.CAR);

	private handling: VehicleHandling = {
		['fMass']: 1600,
		['vecCentreOfMassOffset']: {
			x: 0,
			y: 0.10999999940395355,
			z: 0.25999999046325684,
		},
		['vecSeatOffsetDist']: {
			x: 0,
			y: 0,
			z: 0,
		},
		['fPercentSubmerged']: 85,
		['fDriveBiasFront']: 0,
		['nInitialDriveGears']: 5,
		['vecInertiaMultiplier']: {
			x: 1,
			y: 1.2000000476837158,
			z: 1.399999976158142,
		},
		['fInitialDragCoeff']: 5.649999948218465,
		['fDriveInertia']: 1,
		['fClutchChangeRateScaleUpShift']: 1.600000023841858,
		['fClutchChangeRateScaleDownShift']: 1.600000023841858,
		['fInitialDriveForce']: 0.8799999952316284,
		['fInitialDriveMaxFlatVel']: 100,
		['fBrakeForce']: 0.8999999761581421,
		['fBrakeBiasFront']: 0.42500001192092896,
		['fHandBrakeForce']: 0.6000000238418579,
		['fSteeringLock']: 40.00000002799394,
		['fSteeringLockRatio']: 1.4323945045471191,
		['fTractionCurveMax']: 2.450000047683716,
		['fTractionCurveMaxRatio']: 0.40816324949264526,
		['fTractionCurveMin']: 2.25,
		['fTractionCurveMinRatio']: 4.999999046325684,
		['fTractionCurveLateral']: 22.500001296407103,
		['fTractionCurveLateralRatio']: 2.5464789867401123,
		['fTractionSpringDeltaMax']: 0.15000000596046448,
		['fTractionSpringDeltaMaxRatio']: 6.666666507720947,
		['fLowSpeedTractionLossMult']: 1,
		['fTractionBiasFront']: 0.49000000953674316,
		['fTractionLossMult']: 1,
		['fSuspensionForce']: 1.899999976158142,
		['fSuspensionCompDamp']: 0.8000000566244125,
		['fSuspensionReboundDamp']: 1.5000000596046448,
		['fSuspensionUpperLimit']: 0.09000000357627869,
		['fSuspensionLowerLimit']: -0.14000000059604645,
		['fSuspensionRaise']: -0.009999999776482582,
		['fSuspensionBiasFront']: 0.5699999928474426,
		['fAntiRollBarForce']: 0.800000011920929,
		['fAntiRollBarBiasFront']: 0.6000000238418579,
		['fRollCentreHeightFront']: 0.3400000035762787,
		['fRollCentreHeightRear']: 0.3499999940395355,
		['fCollisionDamageMult']: 0.699999988079071,
		['fWeaponDamageMult']: 1,
		['fDeformationDamageMult']: 0.699999988079071,
		['fEngineDamageMult']: 1.5,
		['fPetrolTankVolume']: 65,
		['fOilVolume']: 5,
		['nMonetaryValue']: 100,
		['strModelFlags']: 4456464,
		['strHandlingFlags']: 131074,
		['strDamageFlags']: 0,
		['strAdvancedFlags']: 983040,
	};
	private readonly lockState = new Subject(false);
	private readonly lightsState = new Subject(true);
	private readonly seatbeltState = new Subject(true);
	private readonly engineState = new Subject(true);
	private readonly rpm = new Subject(0.24);
	private readonly turnLightsState = new Subject<IVehicleTurnLightsState>({
		left: true,
		right: false,
	});
	private readonly inVeh = new Subject(true);
	private readonly isDriver = new Subject(true);
	private readonly localVehicleName = new Subject('Infernus');
	private readonly localVehicleModel = new Subject('IFN');
	private readonly vehicleModels = new Subject<
		IClientConfigs['vehicles']['models']
	>([
		{
			model: 'bmw_m5f90',
			name: 'BMW M5 F90',
			brandName: 'BMW',
			modelName: 'M5',
			brandNameShort: 'BMW',
			bodyType: 'sedan',
			enginePower: 600,
			priceCash: 15000000,
			priceDonate: 5000,
			shopId: null,
			fuelConsumptionRate: 12,
			fuelTankCapacity: 80,
			idealFuelType: VehicleFuelType.Petrol95,
			engineType: VehicleEngineType.Petrol,
			maxSpeed: 250,
			trunkVolume: 500,
			passengerSeats: 5,
			class: VehicleClass.A,
			type: VehicleType.CAR,
		},
		{
			model: 'mercedes_e63s',
			name: 'Mercedes E63s AMG',
			brandName: 'Mercedes-Benz',
			modelName: 'E63s',
			brandNameShort: 'MB',
			bodyType: 'sedan',
			enginePower: 612,
			priceCash: 18000000,
			priceDonate: 6000,
			shopId: null,
			fuelConsumptionRate: 11,
			fuelTankCapacity: 75,
			idealFuelType: VehicleFuelType.Petrol95,
			engineType: VehicleEngineType.Petrol,
			maxSpeed: 250,
			trunkVolume: 450,
			passengerSeats: 5,
			class: VehicleClass.A,
			type: VehicleType.CAR,
		},
		{
			model: 'audi_rs6',
			name: 'Audi RS6 Performance',
			brandName: 'Audi',
			modelName: 'RS6',
			brandNameShort: 'Audi',
			bodyType: 'sedan',
			enginePower: 605,
			priceCash: 16000000,
			priceDonate: 5500,
			shopId: null,
			fuelConsumptionRate: 11.5,
			fuelTankCapacity: 70,
			idealFuelType: VehicleFuelType.Petrol95,
			engineType: VehicleEngineType.Petrol,
			maxSpeed: 250,
			trunkVolume: 480,
			passengerSeats: 5,
			class: VehicleClass.A,
			type: VehicleType.CAR,
		},
		{
			model: 'porsche_911_gt3',
			name: 'Porsche 911 GT3',
			brandName: 'Porsche',
			modelName: '911 GT3',
			brandNameShort: 'Porsche',
			bodyType: 'coupe',
			enginePower: 510,
			priceCash: 25000000,
			priceDonate: 8000,
			shopId: null,
			fuelConsumptionRate: 13,
			fuelTankCapacity: 64,
			idealFuelType: VehicleFuelType.Petrol95,
			engineType: VehicleEngineType.Petrol,
			maxSpeed: 320,
			trunkVolume: 260,
			passengerSeats: 2,
			class: VehicleClass.A,
			type: VehicleType.CAR,
		},
		{
			model: 'lada_vesta',
			name: 'Lada Vesta',
			brandName: 'Lada',
			modelName: 'Vesta',
			brandNameShort: 'Lada',
			bodyType: 'sedan',
			enginePower: 106,
			priceCash: 700000,
			priceDonate: 0,
			shopId: 'simple_shop',
			fuelConsumptionRate: 7,
			fuelTankCapacity: 50,
			idealFuelType: VehicleFuelType.Petrol95,
			engineType: VehicleEngineType.Petrol,
			maxSpeed: 180,
			trunkVolume: 480,
			passengerSeats: 5,
			class: VehicleClass.D,
			type: VehicleType.CAR,
		},
		{
			model: 'gaz_2410',
			name: 'GAZ-2410',
			brandName: 'GAZ',
			modelName: '2410',
			brandNameShort: 'GAZ',
			bodyType: 'sedan',
			enginePower: 100,
			priceCash: 300000,
			priceDonate: 0,
			shopId: 'simple_shop',
			fuelConsumptionRate: 10,
			fuelTankCapacity: 60,
			idealFuelType: VehicleFuelType.Petrol95,
			engineType: VehicleEngineType.Petrol,
			maxSpeed: 160,
			trunkVolume: 500,
			passengerSeats: 5,
			class: VehicleClass.D,
			type: VehicleType.CAR,
		},
		{
			model: 'ford_mustang',
			name: 'Ford Mustang GT',
			brandName: 'Ford',
			modelName: 'Mustang',
			brandNameShort: 'Ford',
			bodyType: 'coupe',
			enginePower: 450,
			priceCash: 12000000,
			priceDonate: 4000,
			shopId: null,
			fuelConsumptionRate: 12,
			fuelTankCapacity: 70,
			idealFuelType: VehicleFuelType.Petrol95,
			engineType: VehicleEngineType.Petrol,
			maxSpeed: 250,
			trunkVolume: 380,
			passengerSeats: 4,
			class: VehicleClass.A,
			type: VehicleType.CAR,
		},
		{
			model: 'chevrolet_camaro',
			name: 'Chevrolet Camaro SS',
			brandName: 'Chevrolet',
			modelName: 'Camaro',
			brandNameShort: 'Chevy',
			bodyType: 'coupe',
			enginePower: 455,
			priceCash: 11000000,
			priceDonate: 3500,
			shopId: null,
			fuelConsumptionRate: 11,
			fuelTankCapacity: 72,
			idealFuelType: VehicleFuelType.Petrol95,
			engineType: VehicleEngineType.Petrol,
			maxSpeed: 250,
			trunkVolume: 390,
			passengerSeats: 4,
			class: VehicleClass.A,
			type: VehicleType.CAR,
		},
	]);

	constructor() {
		const iterCount = 50;
		for (let i = 0; i < iterCount; i++) {
			this.playerVehiclesStub.push({
				id: `id#${i}`,
				source: PlayerVehicleSource.Owned,
				name: `Vehicle#${i}`,
				type: Math.random() > 0.25 ? 'automobile' : 'bike',
				numberPlate:
					Math.random() > 0.25
						? {
								type:
									Math.random() > 0.5
										? VehicleNumberPlateType.Classic
										: VehicleNumberPlateType.ClassicSquareNoFlag,
								text: 'o123oo',
								region: '123',
						  }
						: {
								type: VehicleNumberPlateType.Custom,
								text: 'CCDPLANET',
								region: null,
						  },
				isSpawned: i < 2,
				isLocked: Math.random() > 0.75,
				isRegistered: Math.random() > 0.75,
				isFavorite: Math.random() > 0.9,
				model: `config#${i}`,
			});
		}
		this.playerVehiclesStub.push({
			id: 'abcd-efgh-ijkl-mnop',
			source: PlayerVehicleSource.Owned,
			name: 'Vehicle Test',
			type: 'automobile',
			numberPlate: {
				type: VehicleNumberPlateType.Classic,
				text: 'o138ao',
				region: '138',
			},
			isSpawned: true,
			isLocked: true,
			isRegistered: true,
			isFavorite: true,
			model: 'config#test',
		});

		this.workVehiclesStub.push({
			id: 'work-vehicle#1',
			source: PlayerVehicleSource.Job,
			vehicleNetId: 1,
			name: 'Work Vehicle',
			type: 'automobile',
			numberPlate: {
				type: VehicleNumberPlateType.Classic,
				text: 'A001AA',
				region: '777',
			},
			isSpawned: false,
			isLocked: true,
			isRegistered: true,
			isFavorite: false,
			model: 'config#work',
		});

		this.updateOwnedVehicleList();
		this.updateWorkVehicleList();

		setInterval(() => {
			this.speed.next(Math.sin(Date.now() / 1000) * 100 + 100);
		}, 1000 / 45);
	}

	public getLocalVehicleName(): IObservable<string | null> {
		return this.localVehicleName;
	}

	public getLocalVehicleModel(): IObservable<string | null> {
		return this.localVehicleModel;
	}

	public isPlayerInVehicle(): IObservable<boolean> {
		return this.inVeh;
	}

	public isPlayerVehicleDriver(): IObservable<boolean> {
		return this.isDriver;
	}

	public getLocalVehicleTurnLightsState(): IObservable<IVehicleTurnLightsState> {
		return this.turnLightsState;
	}

	public getLocalVehicleFuel() {
		return this.fuel;
	}

	public stubDrainLocalFuel(amount?: number): void {
		const current = this.fuel.getValue();
		const next =
			amount === undefined
				? 0
				: Math.max(0, Math.floor(current - Math.max(0, amount)));
		this.fuel.next(next);
	}

	public getLocalVehicleFuelTankCapacity() {
		return this.fuelTankCapacity;
	}

	public getLocalVehicleEngineType() {
		return this.engineType;
	}

	public getLocalVehicleMileage() {
		return this.mileage;
	}

	public getPlayerVehicleList() {
		return this.playerVehicles;
	}

	public getPlayerWorkVehicleList() {
		return this.workVehicles;
	}

	public getVehicleBrandNameByModel(_model: string): string | null {
		return 'BMW';
	}

	public getVehicleModelNameByModel(_model: string): string | null {
		return 'M5 F90';
	}

	public getVehicleNameByModel(_model: string): string | null {
		return 'BMW M5 F90';
	}

	public getVehicleModels(): IObservable<IClientConfigs['vehicles']['models']> {
		return this.vehicleModels;
	}

	public isVehiclePending(): boolean {
		return false;
	}

	public async setVehicleFavorite(id: string, state: boolean): Promise<void> {
		console.log('setVehicleFavorite', { id, state });
		const vehicle = this.getPlayerVehicleById(id);
		if (vehicle?.source === PlayerVehicleSource.Owned) {
			vehicle.isFavorite = state;
			this.updateOwnedVehicleList();
		}
	}

	public async spawnPlayerVehicle(id: string): Promise<void> {
		console.log('spawnPlayerVehicle', { id });
		const vehicle = this.getPlayerVehicleById(id);
		if (vehicle) {
			vehicle.isSpawned = true;
			this.updateOwnedVehicleList();
			this.updateWorkVehicleList();
		}
	}

	public async destroyPlayerVehicle(id: string): Promise<void> {
		console.log('destroyPlayerVehicle', { id });
		const vehicle = this.getPlayerVehicleById(id);
		if (vehicle) {
			vehicle.isSpawned = false;
			this.updateOwnedVehicleList();
			this.updateWorkVehicleList();
		}
	}

	public async destroyAllPlayerVehicles(): Promise<void> {
		console.log('destroyAllPlayerVehicles');
		this.playerVehiclesStub.forEach((vehicle) => (vehicle.isSpawned = false));
		this.updateOwnedVehicleList();
	}

	public async setPlayerVehicleLocked(
		id: string,
		locked: boolean,
	): Promise<void> {
		console.log('setPlayerVehicleLocked', { id, locked });
		const vehicle = this.getPlayerVehicleById(id);
		if (vehicle) {
			vehicle.isLocked = locked;
			this.updateOwnedVehicleList();
			this.updateWorkVehicleList();
		}
	}

	public getLocalVehicleSpeed(): IObservable<number> {
		return this.speed;
	}

	public getLocalVehicleMaxSpeed(): IObservable<number> {
		return this.maxSpeed;
	}

	public getLocalVehicleHandlingMode(): IObservable<HandlingMode> {
		return this.handlingMode;
	}

	public getLocalVehicleVehicleType(): IObservable<VehicleType> {
		return this.localVehicleType;
	}

	public async setLocalVehicleHandlingMode(mode: HandlingMode): Promise<void> {
		this.handlingMode.next(mode);
	}

	public async getLocalVehicleHandling(): Promise<VehicleHandling> {
		return this.handling;
	}

	public async getLocalVehicleStockHandling(): Promise<VehicleHandling> {
		return this.handling;
	}

	public async setLocalVehicleHandling(
		handling: VehicleHandling,
		_mode?: HandlingMode,
	): Promise<void> {
		this.handling = handling;
	}

	public getLocalVehicleLockState(): IObservable<boolean> {
		return this.lockState;
	}

	public getLocalVehicleLightsState(): IObservable<boolean> {
		return this.lightsState;
	}

	public getSeatbeltState(): IObservable<boolean> {
		return this.seatbeltState;
	}

	public getEngineState(): IObservable<boolean> {
		return this.engineState;
	}

	public getLocalVehicleEngineRpm(): IObservable<number> {
		return this.rpm;
	}

	public getLocalOwnedVehicleId(): IObservable<string | null> {
		return this.vehicleId;
	}

	public getLocalVehicleNetId(): IObservable<number | null> {
		return this.vehicleNetId;
	}

	public isLocalVehicleClientSide(): boolean {
		const vehicleNetId = this.getLocalVehicleNetId().getValue();
		return vehicleNetId === 65535;
	}

	private getPlayerVehicleById(id: string): IPlayerVehicleListItem | null {
		const vehicle =
			this.playerVehiclesStub.find((entry) => entry.id === id) ??
			this.workVehiclesStub.find((entry) => entry.id === id);

		if (!vehicle) {
			return null;
		}

		return vehicle;
	}

	private updateOwnedVehicleList() {
		this.playerVehicles.next(
			this.playerVehiclesStub.map((vehicle) => {
				return { ...vehicle };
			}),
		);
	}

	private updateWorkVehicleList() {
		this.workVehicles.next(
			this.workVehiclesStub.map((vehicle) => {
				return { ...vehicle };
			}),
		);
	}

	public getGovernmentPriceByModel(_model: string): number {
		return 1_000_000;
	}
}
